/**
 * Left-side navigation panel for the Auth Letter Upload modal.
 * Renders one NavMenuItem per authorization plus an "Add More" button.
 * Pure presentation — all state lives in the parent modal.
 */
import { NavMenuItem } from "@risalabs_frontend_org/oasis-ui-kit";
import { AuthLetterUploadFormState } from "../../../data-model/candidAuthorizationModel";
import { Delete } from "../../../svg/delete";
import { PlusCircle } from "../../../svg/plusCircle";

/**
 * Props for {@link AuthLetterNavSidebar}.
 */
interface AuthLetterNavSidebarProps {
  /** Authorization tabs to render as sidebar items. */
  items: AuthLetterUploadFormState[];
  /** Index of the currently active tab. */
  selectedIndex: number;
  /** Set of tab indexes that should be disabled. */
  disabledIndexes: Set<number>;
  /**
   * Invoked when the user clicks a tab.
   *
   * @param index - Index of the tab that was selected
   */
  onSelect: (index: number) => void;
  /**
   * Invoked when the user clicks the inline delete icon.
   *
   * @param index - Index of the tab to remove
   */
  onDelete: (index: number) => void;
  /** Invoked when the user clicks the "Add More" entry. */
  onAddMore: () => void;
  /** When true, the current tab is incomplete so "Add More" is hidden. */
  isSaveDisabled: boolean;
}

const BASE_CLASS_NAME = "auth-letter-upload-modal";

/**
 * Sidebar list of authorization tabs with inline delete and an Add More entry.
 *
 * @param props.items - Authorization records to render as tabs.
 * @param props.selectedIndex - Index of the currently active tab.
 * @param props.disabledIndexes - Set of tab indexes that should be disabled.
 * @param props.onSelect - Invoked when the user clicks a tab.
 * @param props.onDelete - Invoked when the user clicks the inline delete icon.
 * @param props.onAddMore - Invoked when the user clicks the "Add More" entry.
 * @param props.isSaveDisabled - Hides "Add More" while the current tab is incomplete.
 */
const AuthLetterNavSidebar = ({
  items,
  selectedIndex,
  disabledIndexes,
  onSelect,
  onDelete,
  onAddMore,
  isSaveDisabled,
}: AuthLetterNavSidebarProps) => {
  return (
    <div
      className={`${BASE_CLASS_NAME}-left-section w-[20%] shrink-0 border-r border-primaryGray-14`}
    >
      {items.map((data, index) => (
        <NavMenuItem
          key={data.authorizationNumber + index}
          label={data.authorizationNumber}
          onClick={() => onSelect(index)}
          active={selectedIndex === index}
          suffix={
            selectedIndex === index ? (
              <span
                onClick={(e) => e.stopPropagation()}
                role="button"
                tabIndex={-1}
              >
                <Delete
                  height={14}
                  width={14}
                  onClick={() => onDelete(index)}
                />
              </span>
            ) : null
          }
          disabled={disabledIndexes.has(index)}
          className="h-[2.5rem] p-2"
        />
      ))}
      {!isSaveDisabled && (
        <div
          className={`${BASE_CLASS_NAME}-left-section-add-more mt-1 flex cursor-pointer items-center justify-center gap-1 rounded-md p-2 hover:bg-primaryGray-16`}
          onClick={onAddMore}
        >
          <PlusCircle />
          <span className="text-xs font-semibold text-tertiaryBlue-4">
            Add More
          </span>
        </div>
      )}
    </div>
  );
};

export default AuthLetterNavSidebar;
